import type { MeResponse } from '@tabel/shared/schemas/auth';
import type { QuestionsSummary } from '@tabel/shared/schemas/questions';
import { useQuery } from '@tanstack/react-query';
import { Bell, ClipboardList, Clock, MessageCircleQuestion, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CreateTimesheetDialog } from '../../components/CreateTimesheetDialog';
import { DeleteDraftDialog, type DraftToDelete } from '../../components/DeleteDraftDialog';
import { Section, TimesheetCards } from '../../components/TimesheetCards';
import { Button, Card, CardHeader, EmptyState, PageHeader, Steps } from '../../components/ui';
import { api } from '../../lib/api';
import { useSetCrumbs } from '../../lib/crumbs';
import { localToday, ruDate, shownHours } from '../../lib/format';

const CHIP =
  'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 text-[12.5px] font-medium transition-colors max-sm:min-h-10';

/**
 * Сводка о вопросах при входе (003, FR-028; R12): по каждому табелю в работе с вопросами без ответа — их число,
 * новых за вчера и давно без ответа (если есть). Считается сервером в момент показа, без расписания и уведомлений.
 * Без ФИО: названия табелей и числа. Строка открывает табель со списком вопросов, числа — со списком таких вопросов.
 */
function QuestionsDigest({ summary }: { summary: QuestionsSummary | undefined }) {
  const rows = (summary?.timesheets ?? []).filter((t) => t.open > 0 || t.newYesterday > 0 || t.old > 0);
  if (rows.length === 0) return null;
  return (
    <section aria-label="Вопросы без ответа">
      <h2 className="mb-3 flex items-center gap-2 text-[14px] font-semibold text-ink">
        <MessageCircleQuestion size={16} strokeWidth={1.75} className="text-ink-3" aria-hidden />
        Вопросы без ответа
      </h2>
      <Card>
        <ul className="divide-y divide-line">
          {rows.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3 max-sm:px-4">
              <Link
                to={`/timesheets/${t.id}?questions=open`}
                className="min-w-0 flex-1 text-[14px] font-medium text-ink hover:text-accent"
              >
                {t.brigadeName}
                <span className="ml-2 text-[13px] font-normal text-ink-2 tabular-nums">
                  {ruDate(t.periodStart).slice(0, 5)} — {ruDate(t.periodEnd)}
                </span>
              </Link>
              <span className="flex flex-wrap items-center gap-2">
                {t.open > 0 && (
                  <span className="text-[13px] text-ink-2 tabular-nums">Без ответа: {t.open}</span>
                )}
                {t.newYesterday > 0 && (
                  <Link
                    to={`/timesheets/${t.id}?questions=new`}
                    className={`${CHIP} border-accent/20 bg-accent-soft text-accent hover:border-accent/40`}
                  >
                    <Bell size={14} strokeWidth={1.75} aria-hidden />
                    Новых за вчера: {t.newYesterday}
                  </Link>
                )}
                {t.old > 0 && (
                  <Link
                    to={`/timesheets/${t.id}?questions=old`}
                    className={`${CHIP} border-warn/25 bg-warn-soft text-warn hover:border-warn/40`}
                  >
                    <Clock size={14} strokeWidth={1.75} aria-hidden />
                    Давно без ответа: {t.old}
                  </Link>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

/**
 * Список табелей бригадира и создание табеля (FR-015, FR-016): окно «Новый табель» (003 T080) — бригада из бригад
 * бригадира (`me.brigades`), проверка дат и пересечения с табелем той же бригады, источник состава.
 */
export function BrigadierList({ me }: { me?: MeResponse } = {}) {
  useSetCrumbs([{ label: 'Табели' }]);
  const list = useQuery({ queryKey: ['timesheets', 'brigadier'], queryFn: api.list });
  // Сводка о вопросах при входе: не загрузилась (сервер без функции вопросов) — блока нет.
  const digest = useQuery({ queryKey: ['questions', 'summary'], queryFn: api.questionsSummary });
  // Окно создания открывается и из бокового меню («Новый табель») — состояние в адресе (?new=1).
  const [params, setParams] = useSearchParams();
  const open = params.has('new');
  const setOpen = (v: boolean) => setParams(v ? { new: '1' } : {}, { replace: true });
  const [deleting, setDeleting] = useState<DraftToDelete | null>(null);
  const navigate = useNavigate();

  const items = list.data ?? [];
  const working = items.filter((t) => t.status === 'draft' || t.status === 'returned');
  const passed = items.filter((t) => t.status === 'completed' || t.status === 'signed');

  return (
    <>
      <PageHeader
        title="Табели"
        description="Табели ваших бригад. Часы считаются по проходам СКУД, спорные дни отмечены."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setOpen(true)}>
            Новый табель
          </Button>
        }
      />

      {list.isLoading ? (
        <p className="text-[13px] text-ink-3">Загрузка…</p>
      ) : items.length === 0 ? (
        <div className="grid grid-cols-2 gap-4 max-wide:grid-cols-1">
          <Card>
            <EmptyState
              icon={ClipboardList}
              title="Табелей пока нет"
              description="Создайте табель на бригаду: укажите период и добавьте людей, у которых есть проходы на объекте."
              action={
                <Button variant="secondary" icon={Plus} onClick={() => setOpen(true)}>
                  Новый табель
                </Button>
              }
            />
          </Card>
          <Card>
            <CardHeader
              title="Как работает табель"
              description="Четыре шага от проходов до подписи кадров."
            />
            <div className="px-6 pb-6">
              <Steps
                items={[
                  { title: 'Создайте табель', status: 'Название бригады и период — до 31 дня.' },
                  {
                    title: 'Добавьте людей',
                    status: 'Список берётся из СКУД: видно, кто и сколько дней был на объекте.',
                  },
                  {
                    title: 'Проверьте часы',
                    status:
                      'Нажмите на ячейку — справа откроется карточка дня: проходы и расчёт. Исправить часы можно с комментарием.',
                  },
                  {
                    title: 'Завершите',
                    status: 'Табель уходит кадрам. Они подпишут или вернут с комментарием.',
                  },
                ]}
              />
            </div>
          </Card>
        </div>
      ) : (
        <>
          <QuestionsDigest summary={digest.data} />
          {working.length > 0 && (
            <Section title="В работе" count={working.length}>
              <TimesheetCards
                items={working}
                onDelete={(t) =>
                  setDeleting({
                    ...t,
                    people: t.peopleCount ?? 0,
                    hours: t.totalHours === undefined ? null : shownHours(t.totalValue, t.totalHours),
                    openQuestions: t.openQuestions,
                  })
                }
              />
            </Section>
          )}
          {passed.length > 0 && (
            <Section title="Переданы кадрам" count={passed.length}>
              <TimesheetCards items={passed} />
            </Section>
          )}
        </>
      )}

      <DeleteDraftDialog draft={deleting} onClose={() => setDeleting(null)} screen="BrigadierList" />

      <CreateTimesheetDialog
        open={open}
        onClose={() => setOpen(false)}
        brigades={me?.brigades}
        sources={items}
        today={localToday(me?.object.tzOffset)}
        // Окно закрыто до перехода: «Назад» из нового табеля ведёт в список, а не в окно.
        onCreated={(id) => {
          setOpen(false);
          navigate(`/timesheets/${id}`);
        }}
      />
    </>
  );
}
